import './App.css';
import './styles/global-theme.scss';

import React, { Suspense, lazy } from 'react';

import { useSelector } from 'react-redux';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';

import { ThemeProvider } from '@mui/material/styles';

import { ThemeProvider as CustomThemeProvider } from './contexts/ThemeContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { LocaleProvider, useLocale } from './contexts/LocaleContext';
import Footer from './components/Footer/Footer';
import NavBarV2 from './components/NavBar/NavBarV2';
import theme from './theme'; // path to your theme file
import CityLaunchGate from './Views/PreLaunch/CityLaunchGate';
import LoginForm from './Views/Login/LoginForm';
import { isSpeedDateEnabled } from './config/featureFlags';
import RegisterGate from './Views/Auth/Register/RegisterGate';
import OTPVerification from './components/SignUp/OTPVerification';

const lazyNamed = (loader, name) => lazy(() =>
  loader().then((module) => ({ default: module[name] }))
);

const NotFound = lazy(() => import('./Views/404/404'));
const About = lazy(() => import('./Views/About/About'));
const Account = lazy(() => import('./Views/Account/Account'));
const Articles = lazyNamed(() => import('./Views/Articles/Articles'), 'Articles');
const Chat = lazy(() => import('./Views/Chat/Chat'));
const DashboardV2 = lazyNamed(() => import('./Views/DashboardV2/DashboardV2'), 'DashboardV2');
const Events = lazy(() => import('./Views/Events/Events'));
const Groups = lazyNamed(() => import('./Views/Groups/Groups'), 'Groups');
const Matches = lazyNamed(() => import('./Views/Matches/Matches'), 'Matches');
const Profile = lazy(() => import('./Views/Profile/Profile'));
const Search = lazyNamed(() => import('./Views/Search/Search'), 'Search');
const SpeedDate = lazyNamed(() => import('./Views/SpeedDate/SpeedDate'), 'SpeedDate');
const ViewAllUsers = lazyNamed(() => import('./Views/ViewAllUsers/ViewAllUsers'), 'ViewAllUsers');
const Contact = lazy(() => import('./Views/Contact/Contact'));
const PrivacyPolicy = lazy(() => import('./Views/PrivacyPolicy/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./Views/TermsOfService/TermsOfService'));
const Success = lazy(() => import('./Views/Subscription/Success'));
const Cancel = lazy(() => import('./Views/Subscription/Cancel'));
const HowItWorks = lazy(() => import('./Views/HomeV2/components/HowItWorks/HowItWorks'));
const Pricing = lazy(() => import('./Views/HomeV2/components/Pricing/Pricing'));
const SuccessStories = lazy(() => import('./Views/HomeV2/components/SuccessStories/SuccessStories'));
const PreOnboard = lazy(() => import('./Views/Auth/Onboard/PreOnboard'));
const Onboard = lazy(() => import('./Views/Auth/Onboard/Onboard'));
const AppleCallback = lazy(() => import('./Views/Auth/AppleCallback'));
const ResetPassword = lazy(() => import('./Views/Auth/ResetPassword/ResetPassword'));
const GoogleCallback = lazy(() => import('./Views/Auth/OAuthCallback/GoogleCallback'));
const FacebookCallback = lazy(() => import('./Views/Auth/OAuthCallback/FacebookCallback'));
const MatchReveal = lazy(() => import('./Views/MatchReveal/MatchReveal'));
const MatchYourLifestyle = lazy(() => import('./components/MatchCard/MatchCard'));
const ReferralDashboard = lazy(() => import('./Views/ReferralDashboard/ReferralDashboard'));
const Leaderboard = lazy(() => import('./Views/Leaderboard/Leaderboard'));

// Loading component for lazy-loaded routes
const LoadingFallback = () => {
  const { t } = useLocale();
  return (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    fontSize: '1.2rem',
    color: 'var(--hh-fg3)'
  }}>
    {t('shell.loading')}
  </div>
  );
};

const ONBOARDING_ROUTES = ['/pre-onboard', '/onboard'];

const ProtectedRoute = () => {
  const userInfo = useSelector((state) => state.user);
  const location = useLocation();

  // Redirects to home if user is not logged in
  if (!userInfo || !userInfo.isAuthenticated || !userInfo.user) {
    return <Navigate to="/" replace />;
  }

  // Unverified users must confirm their email before entering the app.
  // Strict === false so legacy/social-login users without the field aren't blocked.
  if (userInfo.user.isVerified === false) {
    return (
      <Navigate
        to="/verify-otp"
        state={{ email: userInfo.user.email }}
        replace
      />
    );
  }

  const needsOnboarding = userInfo.user.hasCompletedOnboarding === false;
  const onOnboardingRoute = ONBOARDING_ROUTES.includes(location.pathname);

  if (needsOnboarding && !onOnboardingRoute) {
    return <Navigate to="/pre-onboard" replace />;
  }

  // Renders child routes if user is logged in
  return <Outlet />;
};

const App = () => {
  const location = useLocation();
  const isSpeedDatePage = location.pathname.startsWith('/speed-date');
  
  return (
    <LocaleProvider>
    <CustomThemeProvider>
      <SubscriptionProvider>
        <ThemeProvider theme={theme}>
          <div className="page-container">
          {/* Hide navbar on speed dating pages for full-screen experience */}
          {!isSpeedDatePage && <NavBarV2 />}

          <div className="page-content">
            <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route index element={<CityLaunchGate />} />
              <Route path="register" element={<RegisterGate />} />
              <Route path="verify-otp" element={<OTPVerification />} />
              <Route path="login" element={<LoginForm />} />
              <Route path="reset-password" element={<ResetPassword />} />
              <Route path="auth/apple/success" element={<AppleCallback />} />
              <Route path="auth/error" element={<LoginForm />} />
              {/* Protected routes wrapped in a ProtectedRoute component */}
              <Route element={<ProtectedRoute />}>
                <Route path="pre-onboard" element={<PreOnboard />} />
                <Route path="onboard" element={<Onboard />} />
                <Route path="about" element={<About />} />
                <Route path="dashboard" element={<DashboardV2 />} />
                <Route
                  path="referrals"
                  element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ReferralDashboard />
                    </Suspense>
                  }
                />
                <Route
                  path="leaderboard"
                  element={
                    <Suspense fallback={<LoadingFallback />}>
                      <Leaderboard />
                    </Suspense>
                  }
                />
                <Route path="matches" element={<Matches />} />
                <Route path="match-reveal" element={<MatchReveal />} />
                <Route path="profile" element={<Profile />} />
                <Route path="profile/:slug" element={<Profile />} />
                <Route path={"/messages"} element={<Chat />} />
                <Route path={"/account"} element={<Account />} />
                <Route
                  path={"/speed-date"}
                  element={
                    isSpeedDateEnabled() ? (
                      <SpeedDate />
                    ) : (
                      <div style={{ padding: 48, textAlign: 'center' }}>
                        <h2>Speed Date — Coming soon</h2>
                        <p>This feature will be available in a future update.</p>
                      </div>
                    )
                  }
                />
                <Route
                  path={"/speed-date/match/:id"}
                  element={
                    isSpeedDateEnabled() ? (
                      <MatchYourLifestyle />
                    ) : (
                      <div style={{ padding: 48, textAlign: 'center' }}>
                        <h2>Speed Date — Coming soon</h2>
                      </div>
                    )
                  }
                />
                <Route path={"/search"} element={<Search />} />
                <Route path={"/events"} element={<Events />} />
                <Route path={"/articles"} element={<Articles />} />
                <Route path={"/groups"} element={<Groups />} />
                <Route path={"/all-nearby-users"} element={<ViewAllUsers />} />
              </Route>
              {/* Subscription return routes */}
              <Route path="subscription/success" element={<Success />} />
              <Route path="subscription/cancel" element={<Cancel />} />
              <Route path="auth/google/callback" element={<GoogleCallback />} />
              <Route path="auth/facebook/callback" element={<FacebookCallback />} />
              <Route path="how-it-works" element={<HowItWorks />} />
              <Route path="pricing" element={<Pricing />} />
              <Route path="success-stories" element={<SuccessStories />} />
              <Route path="contact" element={<Contact />} />
              <Route path="privacy-policy" element={<PrivacyPolicy />} />
              <Route path="terms-of-service" element={<TermsOfService />} />
              {/* Catch-all route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </div>
          
          {/* Hide footer on speed dating pages for full-screen experience */}
          {!isSpeedDatePage && <Footer />}
          </div>
        </ThemeProvider>
      </SubscriptionProvider>
    </CustomThemeProvider>
    </LocaleProvider>
  );
};

export default App;
